import { useRef, useCallback, useState, useEffect } from 'react';
import { reportApi } from '../services/reportApi';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutosaveOptions {
  reportId: string;
  debounceMs?: number;
}

export function useAutosave({ reportId, debounceMs = 2000 }: UseAutosaveOptions) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const lastSavedContentRef = useRef<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const periodicTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastEditTimeRef = useRef<number>(0);
  const lastPeriodicSnapshotRef = useRef<number>(Date.now());
  const currentContentRef = useRef<string>('');

  const setInitialContent = useCallback((content: string) => {
    lastSavedContentRef.current = content;
    currentContentRef.current = content;
  }, []);

  const performSave = useCallback(async (content: string) => {
    if (content === lastSavedContentRef.current) return;

    setSaveStatus('saving');
    try {
      await reportApi.updateReport(reportId, { content });
      lastSavedContentRef.current = content;
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    }
  }, [reportId]);

  const onContentChange = useCallback((content: string) => {
    currentContentRef.current = content;
    lastEditTimeRef.current = Date.now();

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      performSave(content);
    }, debounceMs);
  }, [performSave, debounceMs]);

  const createSnapshot = useCallback(async (changeType: string, description?: string) => {
    try {
      // Save current content first so the snapshot reflects latest state
      if (currentContentRef.current !== lastSavedContentRef.current) {
        await performSave(currentContentRef.current);
      }
      await reportApi.createVersionSnapshot(reportId, changeType, description);
    } catch (err) {
      console.error('Failed to create version snapshot:', err);
    }
  }, [reportId, performSave]);

  // Periodic snapshot every 5 minutes of editing
  useEffect(() => {
    const PERIODIC_INTERVAL = 5 * 60 * 1000;

    periodicTimerRef.current = setInterval(() => {
      const now = Date.now();
      const hasRecentEdits = (now - lastEditTimeRef.current) < PERIODIC_INTERVAL;
      const enoughTime = (now - lastPeriodicSnapshotRef.current) >= PERIODIC_INTERVAL;

      if (hasRecentEdits && enoughTime && lastSavedContentRef.current) {
        lastPeriodicSnapshotRef.current = now;
        reportApi.createVersionSnapshot(reportId, 'periodic_snapshot', 'Periodic auto-snapshot')
          .catch(err => console.error('Periodic snapshot failed:', err));
      }
    }, 60_000);

    return () => {
      if (periodicTimerRef.current) clearInterval(periodicTimerRef.current);
    };
  }, [reportId]);

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (currentContentRef.current !== lastSavedContentRef.current) {
        reportApi.updateReport(reportId, { content: currentContentRef.current }).catch(() => {});
      }
    };
  }, [reportId]);

  return {
    saveStatus,
    onContentChange,
    setInitialContent,
    createSnapshot,
    forceSave: () => performSave(currentContentRef.current),
  };
}
